import type { VideoModelId } from '@famecreator/stories-core';
import { apiClient } from './client';

import type { SubjectMetaFlatEntry, SubjectMetaGroup } from '@/shared/lib/subject-meta';

/**
 * Ordered subject identity for the SD5 path (SP2 migration). Mirrors
 * `apps/onlygold/src/pages/dashboard/playground/lib/subjects-state.ts`'s
 * `SubjectPayload` union — duplicated here (not imported) because `shared/`
 * doesn't depend on feature code under `pages/`, matching how this file
 * already re-declares rather than imports every other playground-specific
 * shape (e.g. `ref_upload_ids` below). Keep both definitions in sync.
 */
export type SubjectPayload =
  | { type: 'character'; character_id: string }
  | { type: 'reference'; image_urls: string[] };

/**
 * Customer-safe subjects projection echoed on a completed SD5 subject-routed
 * generation. Mirrors the backend's `SubjectPublic` (apps/api/src/inference/
 * subjects/subject-types.ts), persisted to `inferences.subjects_public`.
 * `character_id` here is the INTERNAL character UUID — the same id space
 * `Slot.characterId` uses (see playground/lib/subjects-state.ts) — never the
 * opaque `aurous_id`. `aurous_id` is the `char_<ulid>` used on V1 echoes only;
 * unused on the dashboard. Reference entries never carry the actual upload
 * URLs (only their `image_count`), so `character_id`/`aurous_id` are always
 * null on them — see the "Reuse these subjects" comment in playground/index.tsx
 * for why that makes them structurally un-reusable as slots.
 */
export type SubjectPublicEntry = {
  /**
   * `'target'` (Edit Mode PR-1) is the customer's own photo on an edit-mode
   * (`inference_type: 'i2i'`) row — the SCENE being recreated, not a reference
   * pointed at by the prompt. It leads the array and never carries an id.
   * Anything rendering these entries must branch on it: the reference
   * catch-all mislabels a target as "Photos · 1".
   */
  type: 'character' | 'reference' | 'context' | 'target';
  character_id: string | null;
  aurous_id: string | null;
  image_count: number;
};

export interface CreateGenerationParams {
  /**
   * The user's text. Required for every generation EXCEPT edit mode, where an
   * absent prompt is legal and meaningful: the platform composes the swap
   * instruction and the user's refinement text is optional. Mirrors the
   * backend DTO's `@ValidateIf` exactly (create-inference.dto.ts) — prompt is
   * optional precisely when `target_image` is present.
   */
  prompt?: string;
  negative_prompt?: string;
  /**
   * Style pin — tri-state (style-registers spec 2026-07-24, PR-1 wire
   * contract; mirrors the backend's `CreateInferenceDto.lora_id` exactly,
   * apps/api/src/inference/dto/create-inference.dto.ts):
   *   - omitted (`undefined`) → AUTO — the style dispatcher may match one
   *     from the prompt.
   *   - `null`                → OFF — no style; the prompt renders as
   *     written.
   *   - a style's `id`        → PIN — apply that style's register.
   * Composes with `subjects`/`action_id` below — the style block
   * (playground/components/style-block.tsx, PR-2) never XORs against them,
   * unlike the legacy `character_id`/`reference_image_urls` pair above.
   */
  lora_id?: string | null;
  bundle_id?: string;
  /**
   * Legacy single-character identity — the STYLE-mode path (C final review,
   * SP2 subjects migration). Style/bundle LoRAs run on the old single-
   * identity engine, so selecting one swaps the playground's Subjects rail
   * for the legacy identity block, which posts THIS field (XOR
   * `reference_image_urls` below) alongside `lora_id`/`bundle_id` and the
   * advanced knobs — never alongside `subjects`. Only the plain scene/
   * subjects path (no style selected) uses `subjects` instead. See
   * style-mode-legacy-composer.test.tsx for the payload contract both ways.
   */
  character_id?: string;
  width?: number;
  height?: number;
  steps?: number;
  guidance_scale?: number;
  cfg_rescale?: number;
  denoise_strength?: number;
  seed?: number;
  size?: string;
  /**
   * Number of images to generate, 1-4 (SD5 parallel fan-out). Must reach the
   * API as a JS integer — the backend DTO is `@IsInt()` `@Min(1)` `@Max(4)`
   * (create-inference.dto.ts). A shadcn `Select`'s value is always a string,
   * so callers MUST `Number(v)` before writing this field — a `"2"` 400s.
   * Omitted defaults to 1 server-side.
   */
  count?: number;
  enhance_prompt?: boolean;
  /**
   * Legacy inline-reference identity — the style-mode counterpart of
   * `character_id` above (mutually exclusive with it via the legacy block's
   * own XOR; never sent alongside `subjects`). Populated from the legacy
   * identity block's own uploads, capped at 6 (the legacy DTO limit) — see
   * legacy-identity-block.tsx.
   */
  reference_image_urls?: string[];
  /**
   * Ordered subject identity for the SD5 path (SP2 migration): each entry is
   * a linked character or an inline reference-image set, positionally bound
   * to Image 1..N server-side. The playground sends this INSTEAD of
   * `character_id` / `reference_image_urls` — never alongside them.
   */
  subjects?: SubjectPayload[];
  /**
   * Loose reference images for the SD5 context path (PR-3): ordered server
   * URLs the prompt addresses positionally as Image 1..N. XOR with
   * subjects / character_id / reference_image_urls / lora_id — the
   * composer's reference-board mode sends THIS key and no other identity
   * surface.
   */
  context_images?: string[];
  /**
   * Edit mode (Edit Mode PR-1, spec 2026-09-01 §3.4): the customer's own photo
   * to recreate with an identity swapped in. A `file_<ulid>` id or an https://
   * URL — whatever the ref-upload pipeline produced.
   *
   * Sending it selects an entirely different request shape. It requires
   * exactly one identity (`character_id` or a single `subjects[]` entry) and
   * is REJECTED (400 `parameter_invalid_combination`) alongside
   * `context_images`, `action_id`, `lora_id`/`bundle_id`, or `count > 1` —
   * see normalizeTargetImage on the server. `prompt` becomes optional and
   * `enhance_prompt` is accepted-and-ignored, so the playground's edit branch
   * omits both rather than relying on that leniency.
   */
  target_image?: string;
  /**
   * Output image encoding for an SD5 generation (subjects OR context
   * path) — 'png' when the playground's Transparent-background toggle is
   * on, else 'jpeg'. Ignored on the legacy scene/style path.
   */
  output_format?: 'jpeg' | 'png';
  /**
   * Image-Action pin (SP3-PR-3 Task 6). The tri-state the playground's Action
   * picker produces on the Subjects surface:
   *   - `action_id` set        → PIN this composition act (detection skipped).
   *   - `skip_action_detection`→ NONE — render subjects/prompt exactly, no act.
   *   - both omitted           → AUTO (default) — the server may detect an act.
   * Only ever travels on the Subjects surface (never style/context/video). The
   * internal `create()` reads `action_id` (pin) + `skip_action_detection`
   * (suppress) directly — the public-V1 null-collapse (Decision A) is not the
   * dashboard's contract.
   */
  action_id?: string | null;
  skip_action_detection?: boolean;
}

/**
 * One element of `subjects[]` on a video generation (PR-3a; multi-subject
 * BYO-refs composer, .superpowers/sdd/task-5-ux-spec.md). Presence-XOR, NOT
 * type-discriminated — mirrors the backend's `SubjectInputDto`
 * (apps/api/src/inference/dto/create-video-inference.dto.ts) exactly, unlike
 * the image-side `SubjectPayload` above which uses a `type` field.
 */
export type VideoSubjectInput =
  | { character_id: string }
  | { ref_upload_ids: string[] };

export interface CreateVideoGenerationParams {
  prompt?: string;
  /**
   * Pinned Action id (r2v path). v2: pairs with character_id OR
   * ref_upload_ids OR neither (Action-creates-its-own-subject).
   *
   * TRI-STATE (2026-07-31 seed-leg spec; mirrors the backend DTO's
   * `video_lora_id?: string | null`):
   *   - a string  → pin this Action.
   *   - ABSENT    → auto-match. The server reads the prompt, or — when
   *                 `first_frame_url` rides — what the IMAGE shows.
   *   - `null`    → suppress IMAGE-based matching (plain image-to-video).
   *                 Meaningful only WITH a first frame; without one the
   *                 server treats null exactly like an absent key.
   * `null` is therefore a REAL wire value, not an "omitted" placeholder — the
   * composer sends it structurally, never as `undefined`.
   */
  video_lora_id?: string | null;
  /** Character as the subject. Mutually exclusive with ref_upload_ids
   *  (server 400s parameter_invalid_combination — mirrors image-gen's
   *  character-vs-references rule). */
  character_id?: string;
  /** Inline subject reference uploads (v2 Subject mode): ids returned by
   *  POST /v1/refs/upload. ≤6. Mutually exclusive with character_id. */
  ref_upload_ids?: string[];
  /**
   * PR-3a multi-subject core: ordered subjects (max 2 — `MAX_SUBJECTS`),
   * each a character XOR its own inline-ref group (≤4 refs per element —
   * `SubjectInputDto`'s server-side cap). Mutually exclusive with the legacy
   * `character_id`/`ref_upload_ids` above — the composer sends this INSTEAD
   * of them once a 2nd subject slot is filled, never alongside (both-shapes
   * 400s server-side).
   *
   * Two dispatch shapes (Task 8 delta, "Prompt-Driven Two-Person Mode",
   * backend commit f9b8724c):
   *   - WITH `video_ref_upload_id` (a motion clip attached): role-order
   *     dispatch, unchanged since PR-3a's original ship.
   *   - WITHOUT it (no clip): composes directly from the two subjects' own
   *     reference images — `prompt` becomes REQUIRED in this shape (the only
   *     source of the joint action; server 400s `param: 'prompt'` on empty,
   *     which the composer makes unreachable via the `subjects-need-prompt`
   *     CTA gate).
   */
  subjects?: VideoSubjectInput[];
  /**
   * Interpolation mode (i2v). NEVER travels with character_id /
   * ref_upload_ids — the frame carries the person.
   *
   * `video_lora_id` is the exception, and only on the SEED leg (a first frame
   * with NO last frame): there it carries the Action tri-state above — a pin,
   * an explicit `null` to switch matching off, or no key at all for
   * auto-match. Once `last_frame_url` rides, the Action leg is gone
   * altogether and `video_lora_id` never accompanies it in any form.
   */
  first_frame_url?: string;
  /** Requires first_frame_url (provider constraint). */
  last_frame_url?: string;
  /**
   * SD2 BYO refs (PR-2 Task 7): reference-upload id of a motion clip the
   * generation follows or extends. Mutually exclusive with video_lora_id
   * (an Action) and with first/last frames — the composer clears any pinned
   * Action the moment a clip attaches, and Extend/Interpolation are a mutex
   * pair of disclosures, so the UI never produces a conflicting body.
   */
  video_ref_upload_id?: string;
  /**
   * SD2 BYO refs (PR-2 Task 7): reference-upload id of an audio track to
   * carry into the generation. Requires generate_audio to stay true — the
   * composer forces and locks it while an audio reference is attached.
   */
  audio_ref_upload_id?: string;
  /**
   * SD2 BYO refs (PR-2 Task 7): 'reference' (default — a new scene follows
   * the clip's motion) or 'extend' (continues the clip itself, no
   * subject/model). Only meaningful alongside video_ref_upload_id; the
   * composer omits this field entirely in 'reference' mode (server default)
   * and only ever sends it for 'extend'.
   */
  video_task?: 'reference' | 'extend';
  /** Only meaningful with video_task: 'extend'. Defaults 'forward' server-side. */
  extend_direction?: 'forward' | 'backward';
  resolution?: string;
  ratio?: string;
  duration?: number;
  /** The video model (dashboard id); omitted = the default. Never with an Action (video_lora_id). */
  model?: VideoModelId;
  generate_audio?: boolean;
  watermark?: boolean;
  enhance_prompt?: boolean;
}

export interface Generation {
  id: number;
  aurous_id?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  user_id: string;
  team_id: number;
  prompt?: string;
  negative_prompt?: string;
  width?: number;
  height?: number;
  steps?: number;
  guidance_scale?: number;
  seed?: number;
  /**
   * Output URLs may contain `null` entries for slots still pending —
   * progressive-fill streams partial completions and leaves un-arrived
   * indices as `null` so the playground card can render a Skeleton in
   * that position. The terminal completion event swaps to a fully
   * populated array.
   */
  output_urls?: (string | null)[];
  error_message?: string;
  enhanced_prompt?: string;
  /**
   * The exact text dispatched to the image model: trigger + (enhanced or raw)
   * prompt, plus the Action/subject "Compose the subject shown in Image 1…"
   * composition on the SD5 path. Debug-only + dashboard-only — sanitizeInference
   * keeps it solely for debug callers (showDebugInfo) and ALWAYS strips it on
   * V1 (forV1), so it is absent for non-debug dashboard reads and every
   * customer/API-key/webhook read (apps/api/src/inference/utils/sanitize-response.ts).
   */
  effective_prompt?: string;
  size_preset?: string;
  image_count?: number;
  stored_urls?: string[];
  duration_ms?: number;
  /**
   * Credits charged for this generation (legacy scalar column, dashboard
   * read path). Present on list rows since 2026-07-22 (audit U11, founder-
   * approved) so the viewer modal can show per-generation cost; null/absent
   * on rows minted before cost tracking.
   */
  cost?: number | null;
  bundle_id?: string;
  bundle_name?: string | null;
  character_id?: string | null;
  /**
   * RAW on the dashboard (non-V1) read path by design — sanitizeInference
   * strips this column only when `forV1: true` (apps/api/src/inference/
   * utils/sanitize-response.ts). Null/absent on every generation that never
   * routed through the SD5 subject engine (plain/style images, all videos).
   */
  subjects_public?: SubjectPublicEntry[] | null;
  /**
   * Image-Action echo, RAW on the dashboard (non-V1) read path by design —
   * sanitizeInference keeps `action_lora_id` + `action_meta` when `forV1` is
   * unset and collapses them to the `{id,name}` V1 echo only for API-key /
   * webhook reads (apps/api/src/inference/utils/sanitize-response.ts). Null/
   * absent on every gen that never matched or pinned an act.
   *
   * `action_lora_id` is the matched act's `loras.id` (the same id space the
   * picker's `ImageAction.id` uses — so "Pin it" on the close-the-loop caption
   * can re-select it). `action_meta.name` labels the result-card chip;
   * `action_meta.source` distinguishes an auto-detected act (`'detected'`) from
   * an explicit pin (`'pinned'`) — the chip's `· auto` / `· pinned` affix.
   */
  action_lora_id?: string | null;
  action_meta?: {
    name?: string | null;
    source?: 'detected' | 'pinned' | null;
    mode?: 'bound' | 'generic' | null;
    method?: 'llm' | 'random' | null;
    still_id?: string | null;
    requested_count?: number | null;
    pool_size_at_count?: number | null;
  } | null;
  /**
   * Style-registers echo (PR-2 Task 1), RAW on the dashboard (non-V1) read
   * path by design — mirrors action_lora_id/action_meta immediately above.
   * `style_lora_id` is the applied register's `loras.id` UUID; `style_meta`
   * is `{ aurous_id, name, via }` captured at create() time
   * (apps/api/src/inference/inference.service.ts:1815-1817) —
   * `register_prompt` itself never rides along (admin IP, stripped
   * server-side on every branch). `via` distinguishes a caller-supplied pin
   * (`explicit`), a pin that hit a live successor after one alias hop
   * (`alias_superseded`), and an auto-pick by the style dispatcher
   * (`dispatched`).
   *
   * Selected on BOTH read paths since PR-2 review C1: the single-row read
   * (`getById`, backed by `verifyAccess`'s `select('*')`) AND the `findAll`
   * column list behind `list()` (inference.service.ts — pinned by the
   * "style_lora_id + style_meta + ignored_params ride the SELECT" unit
   * test), beside their act-picker twins `action_lora_id`/`action_meta`. A
   * "Recent Generations" row therefore carries them whenever the DB row
   * does; `deriveStyleLabel()` (design-notes.md §1.7) remains the fallback
   * for HISTORICAL rows minted before style_meta existed.
   */
  style_lora_id?: string | null;
  style_meta?: {
    aurous_id: string;
    name: string | null;
    via: 'explicit' | 'dispatched' | 'alias_superseded';
  } | null;
  /**
   * Knob names accepted-and-ignored on an SD5-routed generation (e.g. a
   * legacy `seed`/`steps` sent alongside a style/plain SD5 request that only
   * honours prompt + size) — raw on the dashboard read path, selected on
   * both getById and list() rows (same PR-2 review-C1 widening as style_meta
   * above; the playground's §1.7 amber note renders from list rows).
   */
  ignored_params?: string[] | null;
  /**
   * V1-shaped style echo `{ id: aurous_id, name } | null` — this is the
   * `forV1`-gated remap `sanitizeInference` produces for the public API
   * (apps/api/src/inference/utils/sanitize-response.ts:409-417) and the
   * `POST /v1/images` 201 body's field. The dashboard `/inference` path
   * (this file) does NOT currently populate this key on any read — it keeps
   * the raw `style_meta` above instead. Declared here for wire-contract
   * parity with `warnings` below and forward-compat should a future change
   * unify the two surfaces; read `style_meta`, not this, until then.
   */
  style?: { id: string; name: string | null } | null;
  /**
   * Non-fatal request-adjustment notices, `{ code, param, message }`
   * (mirrors `apps/api/src/v1/schemas/warning-entry.ts`). Confirmed V1-only
   * today: assembled by `V1Service.resolveStyleWarningsForV1` and attached
   * ONLY to the `POST /v1/images` 201 body and `POST /v1/images/estimate`
   * response (apps/api/src/v1/v1.service.ts) — the dashboard `/inference`
   * create response never sets this key. Declared for the same
   * forward-compat reason as `style` above; playground code that reads it
   * (design-notes.md §1.7) must treat it as always-possibly-undefined.
   */
  warnings?: Array<{ code: string; param: string; message: string }>;
  /**
   * Forensic record of the reference images actually sent — ordered
   * `{framing, source_url, image_n}` (flat, count<=1) or one group per subject
   * (count>=2), each entry carrying a freshly signed `preview_url`.
   *
   * ADMIN/DEBUGGER ONLY on this read path: sanitizeInference keeps the column
   * solely for `showDebugInfo` dashboard callers and ALWAYS strips it on V1 and
   * webhooks (apps/api/src/inference/utils/sanitize-response.ts), so it is
   * absent for every customer read. The playground's generation-viewer modal
   * reads it behind the same `isDebugUser` gate the Provider call block uses.
   */
  subject_meta?: SubjectMetaFlatEntry[] | SubjectMetaGroup[] | null;
  /**
   * Viewer video-inputs (2026-07-22) — the r2v pick trace, ADMIN/DEBUGGER ONLY
   * on this read path (same sanitizer gate as subject_meta above): the picked
   * pool clip's id, the picker's trace, and `template_clip_preview` — a
   * read-time attachment (never a DB column) carrying a freshly signed clip
   * URL + display metadata, absent when signing failed or the clip row was
   * deleted. The generation-viewer modal renders these behind `isDebugUser`.
   */
  template_clip_id?: string | null;
  clip_pick_meta?: {
    method?: string | null;
    reason?: string | null;
    no_match?: boolean;
    picked_clip_id?: string | null;
    umbrella?: { reasoning?: string | null; pool?: string | null; pool_size?: number | null } | null;
  } | null;
  template_clip_preview?: {
    preview_url?: string;
    description: string | null;
    motion: string | null;
    character_count: number;
  } | null;
  loras?: Array<{ id: string; name: string }> | null;
  provider_task_id?: string;
  provider_request_id?: string;
  inference_type?: string;
  reference_image_urls?: string[];
  media_type?: 'image' | 'video';
  /** The video picker's list only: where the generation was made (null: the Playground). */
  made_in?: 'stories' | 'ads' | 'slideshows' | 'carousels' | 'packages' | null;
  /** The video picker's list only: "Story · Scene" for a Stories render, an Ads part's name; null otherwise. */
  made_in_label?: string | null;
  video_url?: string;
  video_duration?: number;
  video_resolution?: string;
  video_ratio?: string;
  video_camera_fixed?: boolean;
  video_lora_name?: string;
  /** Whether the generated video carries synchronized audio (persisted default false). */
  video_generate_audio?: boolean;
  /**
   * BYO-reference-video semantics (viewer-parity 2026-07-21). Set only when the
   * generation carried the customer's own reference video: 'reference' = a fresh
   * scene referencing it; 'extend' = continue it (direction below). Null on
   * every Action / plain / subject-driven video.
   */
  video_task?: 'reference' | 'extend';
  extend_direction?: 'forward' | 'backward';
  /** Customer-supplied reference video/audio URLs — populated on V1-created BYO rows only; null on dashboard rows. */
  reference_video_url?: string | null;
  reference_audio_url?: string | null;
  // Dispatcher debug fields (admin/debugger-only — backend strips for everyone else)
  dispatcher_used?: boolean;
  dispatcher_outcome?: 'matched' | 'no_match' | 'timeout' | 'error' | 'empty_pool' | 'skipped';
  dispatcher_lora_id?: string | null;
  dispatcher_bundle_id?: string | null;
  dispatcher_reasoning?: string | null;
  dispatcher_latency_ms?: number | null;
  dispatcher_cached_tokens?: number | null;
  dispatcher_pool_size?: number | null;
  // Enhancer LLM outcome — customer-facing observability for "why did the
  // enhancer toggle do nothing on this generation?" Used by the playground's
  // post-result indicator. NULL on legacy rows pre-migration; otherwise one
  // of the enum values (scene_* = W6 empty-prompt action gens, where the
  // platform synthesized the scene text now stored on enhanced_prompt).
  enhancer_outcome?: 'no_request' | 'succeeded' | 'identical' | 'refused' | 'transport_error' | 'length_overflow' | 'scene_synthesized' | 'scene_pool' | null;
  // Provider call payloads (admin/debugger-only — backend strips for everyone else).
  // Each is the raw upstream wire payload as a JSON-ish object; null on rows
  // created before payload capture was enabled.
  provider_request_payload?: Record<string, unknown> | null;
  provider_response?: Record<string, unknown> | null;
  created_at: string;
  updated_at?: string;
  /**
   * When the outputs stop being served (30 days after they were copied to the
   * media archive). Absent until the copy lands; the playground then falls
   * back to the provider window (see playground/lib/media-retention.ts).
   */
  media_expires_at?: string;
}

export interface PaginatedGenerations {
  data: Generation[];
  /** Exact for small lists and on the last page; the database's estimate for a large team. */
  total: number;
  /** Rows exist past this page. Absent from an API older than this field. */
  has_more?: boolean;
  page: number;
  limit: number;
}

/**
 * `getNextPageParam` for a `GET /inference` list: follows `has_more`. An API older than that
 * field pages by `total`, which was exact then.
 */
export function nextGenerationsPage(last: PaginatedGenerations, pages: PaginatedGenerations[]): number | undefined {
  if (last.has_more !== undefined) return last.has_more ? pages.length + 1 : undefined;
  const loaded = pages.reduce((n, p) => n + p.data.length, 0);
  return last.data.length > 0 && loaded < last.total ? pages.length + 1 : undefined;
}

/**
 * The count to show for the loaded pages of a `GET /inference` list. `total` is the database's
 * estimate for a large team, so once the last page is in, the rows loaded are the count; until
 * then it is never below one past them. Page 1's total also carries the rows live updates
 * prepended (generation-cache.ts).
 */
export function generationsTotal(pages: PaginatedGenerations[], loaded: number): number {
  const last = pages[pages.length - 1];
  if (!last) return 0;
  if (last.has_more === false) return loaded;
  return Math.max(pages[0].total, last.total, last.has_more ? loaded + 1 : 0);
}

/** Half-open `[start, end)` created_at window, as RFC 3339 instants. */
export interface GenerationsRange {
  start: string;
  end: string;
}

export type GenerationRowsCache<T> = { data: T[] } | { pages: Array<{ data: T[] }>; pageParams: unknown[] };

/**
 * Apply `fn` to the generation rows of any `['generations', …]` cache entry,
 * whichever shape it has: a plain page (`listQueryOptions`) or infinite pages
 * (`historyInfiniteQueryOptions`). Returns `old` unchanged when `fn` leaves
 * every page's array identical, so `setQueriesData` doesn't re-render.
 */
export function mapCachedGenerations<T, C extends GenerationRowsCache<T> = GenerationRowsCache<T>>(
  old: C | undefined,
  fn: (rows: T[]) => T[],
): C | undefined {
  if (!old) return old;
  if ('pages' in old) {
    let changed = false;
    const pages = old.pages.map((p) => {
      const data = fn(p.data);
      if (data === p.data) return p;
      changed = true;
      return { ...p, data };
    });
    return changed ? ({ ...old, pages } as C) : old;
  }
  if (!Array.isArray(old.data)) return old;
  const data = fn(old.data);
  return data === old.data ? old : ({ ...old, data } as C);
}

export const generationService = {
  async create(params: CreateGenerationParams): Promise<{ data: Generation }> {
    const response = await apiClient.post<{ data: Generation }>('/inference', params);
    return response.data;
  },

  /**
   * `signal`: React Query's AbortSignal. An infinite query re-fetches its loaded
   * pages one after another; when a newer refetch supersedes it, the signal
   * stops the remaining page requests instead of letting them run to the end.
   */
  async list(page = 1, limit = 20, range?: GenerationsRange, signal?: AbortSignal): Promise<PaginatedGenerations> {
    const response = await apiClient.get<PaginatedGenerations>('/inference', {
      params: {
        page,
        limit,
        ...(range && { start_time: range.start, end_time: range.end }),
      },
      signal,
    });
    return response.data;
  },

  /**
   * The team's videos for the video picker (Stories takes, Ads parts): every finished-or-not video,
   * the ones made inside Stories, Ads, Slideshows and Carousels included, each with `made_in`.
   */
  async listVideos(page = 1, limit = 24, signal?: AbortSignal): Promise<PaginatedGenerations> {
    const response = await apiClient.get<PaginatedGenerations>('/inference', {
      params: { page, limit, media_type: 'video', everywhere: true },
      signal,
    });
    return response.data;
  },

  /**
   * Query options for the generations history list. The pagination params
   * MUST be part of the key: observers sharing a key share one cache entry,
   * and whichever subscribes first supplies the queryFn for all of them.
   * Keep the 'generations' prefix — WS invalidation and the partial-output
   * merge in InferenceNotifications match on it. `this` (not the module
   * const) so test mocks that override `list` are honored; call it as a
   * method, don't destructure it.
   */
  listQueryOptions(page = 1, limit = 20) {
    return {
      queryKey: ['generations', { page, limit }] as const,
      queryFn: () => this.list(page, limit),
    };
  },

  /**
   * Infinite-query options for the playground history ("Load more" + day
   * filter). Same 'generations' prefix contract as `listQueryOptions`, but
   * the cached value is `InfiniteData<PaginatedGenerations>` — cache writers
   * matching the prefix must go through `mapCachedGenerations`. `range` is
   * part of the key so each day gets its own entry.
   */
  historyInfiniteQueryOptions(limit: number, range?: GenerationsRange) {
    return {
      queryKey: ['generations', 'history', { limit, range: range ?? null }] as const,
      queryFn: ({ pageParam, signal }: { pageParam: number; signal?: AbortSignal }) =>
        this.list(pageParam, limit, range, signal),
      initialPageParam: 1,
      getNextPageParam: nextGenerationsPage,
    };
  },

  async getById(id: string): Promise<Generation> {
    // `id` should be the opaque aurous_id (`img_*`/`vid_*`): the dashboard
    // GET routes through verifyAccess, which is opaque-id-only (PR-3) — a
    // bare int row id is a guaranteed 404. `_silent` because the callers are
    // background refreshes (post-create reconcile, WebSocket status pushes,
    // shared/api/generation-cache.ts): a failure must never surface the
    // global interceptor toast. The timeout frees refreshGeneration's
    // one-request-per-row slot if a request hangs.
    const response = await apiClient.get<Generation>(`/inference/${id}`, {
      _silent: true,
      timeout: 15_000,
    } as Parameters<typeof apiClient.get>[1]);
    return response.data;
  },

  async createVideo(params: CreateVideoGenerationParams): Promise<{ data: Generation }> {
    const response = await apiClient.post<{ data: Generation }>('/inference/video', params);
    return response.data;
  },

  async cancel(id: string): Promise<void> {
    await apiClient.post(`/inference/${id}/cancel`);
  },
};
